import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useTranslation from 'components/FloatingResourceAddMenu/hooks/useTranslation';
import CreateApplicationForm from 'components/forms/CreateApplicationForm';
import CreateAssetForm from 'components/forms/CreateAssetForm';
import CreateBoundaryForm from 'components/forms/CreateBoundaryForm';
import CreateClientForm from 'components/forms/CreateClientForm';
import CreateFarmForm from 'components/forms/CreateFarmForm';
import CreateFieldForm from 'components/forms/CreateFieldForm';
import InviteUserForm from 'components/forms/InviteUserForm';

import { FLOATING_RESOURCE_ADD_MENU_OPTION } from './constants';
import type { FloatingResourceAddOption, Props } from './types';

const useData = ({ onClose }: Props) => {
    const navigate = useNavigate();

    const {
        APPLICATION_LABEL,
        ASSET_LABEL,
        BOUNDARY_LABEL,
        CLIENT_LABEL,
        FARM_LABEL,
        FIELD_LABEL,
        IMPORT_LABEL,
        MEMBER_LABEL,
        PRESCRIPTION_LABEL
    } = useTranslation();

    const [selectedResourceValue, setSelectedResourceValue] =
        useState<FLOATING_RESOURCE_ADD_MENU_OPTION | null>(null);

    const [requestingClose, setRequestingClose] = useState(false);

    const handleRequestClose = () => {
        if (selectedResourceValue) {
            setRequestingClose(true);
        } else {
            onClose();
        }
    };

    const handleCancelClose = () => {
        setRequestingClose(false);
    };

    const handleConfirmedClose = () => {
        setSelectedResourceValue(null);
        setRequestingClose(false);
    };

    const handleBackToMenu = () => {
        setRequestingClose(true);
    };

    const handleSelectResource = (resourceValue: FLOATING_RESOURCE_ADD_MENU_OPTION) => {
        if (resourceValue === FLOATING_RESOURCE_ADD_MENU_OPTION.IMPORT) {
            navigate('/import');
            onClose();

            return;
        }

        if (resourceValue === FLOATING_RESOURCE_ADD_MENU_OPTION.PRESCRIPTION) {
            navigate('/data/prescriptions/new');
            onClose();

            return;
        }

        setSelectedResourceValue(resourceValue);
        setRequestingClose(false);
    };

    const menuOptions: FloatingResourceAddOption[] = [
        {
            label: CLIENT_LABEL,
            renderComponent: () => (
                <CreateClientForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.CLIENT
        },
        {
            label: FARM_LABEL,
            renderComponent: () => (
                <CreateFarmForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.FARM
        },
        {
            label: MEMBER_LABEL,
            renderComponent: () => (
                <InviteUserForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.MEMBER
        },
        {
            label: FIELD_LABEL,
            renderComponent: () => (
                <CreateFieldForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.FIELD
        },

        {
            label: ASSET_LABEL,
            renderComponent: () => (
                <CreateAssetForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.ASSET
        },
        {
            label: APPLICATION_LABEL,
            renderComponent: () => (
                <CreateApplicationForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.APPLICATION
        },
        {
            label: IMPORT_LABEL,
            renderComponent: () => null,
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.IMPORT
        },
        {
            label: BOUNDARY_LABEL,
            renderComponent: () => (
                <CreateBoundaryForm
                    onCancelClose={handleCancelClose}
                    onClose={handleConfirmedClose}
                    requestingClose={requestingClose}
                />
            ),
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.BOUNDARY
        },
        {
            label: PRESCRIPTION_LABEL,
            renderComponent: () => null,
            value: FLOATING_RESOURCE_ADD_MENU_OPTION.PRESCRIPTION
        }
    ];

    const selectedResource =
        menuOptions.find(({ value }) => value === selectedResourceValue) ?? null;

    return {
        handleBackToMenu,
        handleRequestClose,
        handleSelectResource,
        menuOptions,
        selectedResource
    };
};

export default useData;
